import { useState, useEffect, useRef } from 'react';
import { FileText, Download, ChevronDown } from 'lucide-react';
import { platformSettingsAPI, fundsAPI } from '../../../../../services';

const defaultEnd = new Date().toISOString().slice(0, 10);
const defaultStart = new Date(Date.now() - 15 * 86400000).toISOString().slice(0, 10);

const stripSymbol = (s) => {
  if (!s) return s;
  const parts = s.split('.');
  return parts.length > 1 ? parts[parts.length - 1] : s;
};

const formatPrice = (v) => {
  if (v == null || isNaN(v)) return '0';
  return Number(v).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 5
  });
};

const fmtCurrency = (v, cur, d = 2) => {
  const n = Number(v || 0);
  return `${cur ? cur + ' ' : ''}${n.toFixed(d)}`;
};

const StatementPanel = ({ userId, userEmail, formatDate, formatCurrency, onGenerateStatement }) => {
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [loading, setLoading] = useState(false);
  const [statement, setStatement] = useState(null);
  const [error, setError] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [platformName, setPlatformName] = useState('');
  const menuRef = useRef(null);

  useEffect(() => {
    platformSettingsAPI.get().then(res => {
      const data = res?.data?.data || res?.data;
      if (data?.platformName) setPlatformName(data.platformName);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleGenerate = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }
    setError('');
    setLoading(true);
    setStatement(null);
    try {
      const res = await onGenerateStatement(userId, startDate, endDate);
      if (res?.success && res?.data?.data) {
        setStatement(res.data.data);
      } else {
        setError(res?.data?.info || 'Failed to generate statement');
      }
    } catch (err) {
      setError(err?.response?.data?.details || err.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const baseFilename = () => `statement-${userId}-${startDate}-to-${endDate}`;
  const displayName = () => {
    if (statement?.userName) return statement.userName;
    return userEmail || userId;
  };
  const platform = platformName || 'StandAlone Exchange';

  // ─── CSV Export ───────────────────────────────────────────────────
  const handleExportCSV = () => {
    if (!statement) return;
    setShowExportMenu(false);
    const cur = statement.currency;
    const csvEscape = (v) => `"${String(v).replace(/"/g, '""')}"`;

    const lines = [];
    const add = (row) => lines.push(row.map(csvEscape).join(','));
    const sep = (label) => {
      add([]);
      add([`══════════  ${label}  ══════════`]);
    };
    const hr = () => add(['──────────────────────────────────────────────────']);

    add(['══════════════════════════════════════════════════']);
    add(['            ACCOUNT STATEMENT']);
    add(['══════════════════════════════════════════════════']);
    add([]);

    add(['Platform', platform]);
    add(['User', displayName()]);
    add(['User ID', userId]);
    if (statement.userEmail) add(['Email', statement.userEmail]);
    add(['Period', `${startDate}  —  ${endDate}`]);
    add(['Currency', cur]);
    add(['Generated', new Date().toLocaleString()]);
    add([]);

    sep('SUMMARY');
    add(['Opening Balance', fmtCurrency(statement.openingBalance, statement.openingBalanceCurrency || cur)]);
    add(['Total Credits', fmtCurrency(statement.totalCredits, cur)]);
    add(['Total Debits', fmtCurrency(statement.totalDebits, cur)]);
    add(['Net Change', (statement.netChange >= 0 ? '+' : '') + fmtCurrency(statement.netChange, cur)]);
    add(['Closing Balance', fmtCurrency(statement.closingBalance, statement.closingBalanceCurrency || cur)]);
    add(['Total Entries', String(statement.entryCount)]);

    sep(`TRANSACTIONS (${statement.entries.length})`);
    add(['Date', 'Type', 'Description', 'Credit', 'Debit', 'Balance']);
    hr();
    statement.entries.forEach(e => {
      add([
        formatDate(e.date), e.type, (e.description || ''),
        e.credit > 0 ? fmtCurrency(e.credit, e.creditCurrency || cur) : '-',
        e.debit > 0 ? fmtCurrency(e.debit, e.debitCurrency || cur) : '-',
        fmtCurrency(e.balance, e.balanceCurrency || cur),
      ]);
    });
    hr();
    add(['', '', 'Closing Balance', '', '', fmtCurrency(statement.closingBalance, statement.closingBalanceCurrency || cur)]);

    if (statement.positionsInPeriod && statement.positionsInPeriod.length > 0) {
      sep(`POSITIONS OPENED (${statement.positionsInPeriod.length})`);
      add(['Symbol', 'Side', 'Quantity', 'Entry Price', 'Leverage', 'Opened']);
      hr();
      statement.positionsInPeriod.forEach(p => {
        add([
          stripSymbol(p.symbol), p.side.toUpperCase(), String(p.quantity),
          formatPrice(p.entryPrice), `${p.leverage}x`, formatDate(p.timestamp),
        ]);
      });
    }

    if (statement.ordersInPeriod && statement.ordersInPeriod.length > 0) {
      sep(`ORDERS PLACED (${statement.ordersInPeriod.length})`);
      add(['Symbol', 'Side', 'Type', 'Quantity', 'Price', 'Leverage', 'Status', 'Placed']);
      hr();
      statement.ordersInPeriod.forEach(o => {
        add([
          stripSymbol(o.symbol), o.side.toUpperCase(), o.orderType, String(o.quantity),
          formatPrice(o.price), `${o.leverage}x`, o.status, formatDate(o.timestamp),
        ]);
      });
    }

    if (statement.tradeHistory && statement.tradeHistory.length > 0) {
      sep(`TRADE HISTORY — CLOSED (${statement.tradeHistory.length})`);
      add(['Symbol', 'Side', 'Qty', 'Entry', 'Exit', 'Gross P&L', 'Net P&L', 'Commission', 'Swap', 'Fees', 'Lev', 'Closed']);
      hr();
      statement.tradeHistory.forEach(t => {
        add([
          stripSymbol(t.symbol), t.side.toUpperCase(), String(t.quantity),
          formatPrice(t.entryPrice), formatPrice(t.exitPrice),
          fmtCurrency(t.grossPnl, t.currency || cur),
          fmtCurrency(t.pnl, t.currency || cur),
          fmtCurrency(t.commission, t.currency || cur),
          fmtCurrency(t.swap, t.currency || cur),
          fmtCurrency(t.feesDeducted, t.currency || cur),
          `${t.leverage}x`, formatDate(t.closedAt),
        ]);
      });
    }

    add([]);
    add(['══════════════════════════════════════════════════']);
    add(['          END OF STATEMENT']);
    add(['══════════════════════════════════════════════════']);

    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${baseFilename()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─── PDF Export ───────────────────────────────────────────────────
  const handleExportPDF = async () => {
    if (!statement) return;
    setShowExportMenu(false);
    setLoading(true);
    try {
      const response = await fundsAPI.exportStatement(userId, startDate, endDate, 'pdf');
      const blob = response.data || response;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${baseFilename()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.details || err.message || 'PDF export failed');
    } finally {
      setLoading(false);
    }
  };

  // ─── Excel Export ───────────────────────────────────────────────────
  const handleExportExcel = async () => {
    if (!statement) return;
    setShowExportMenu(false);
    setLoading(true);
    try {
      const response = await fundsAPI.exportStatement(userId, startDate, endDate, 'xlsx');
      const blob = response.data || response;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${baseFilename()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.details || err.message || 'Excel export failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="statement-panel">
      <div className="section-header">
        <FileText size={18} />
        <h3>Statement Generator</h3>
      </div>

      <div className="statement-controls">
        <div className="date-inputs">
          <label>From</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          <label>To</label>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </div>
        <button className="btn btn-primary" onClick={handleGenerate} disabled={loading || !userId}>
          {loading ? 'Generating...' : 'Generate Statement'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {statement && (() => {
        const cur = statement.currency;
        const fmt = (v) => formatCurrency(v, cur);

        return (
        <div className="statement-result">
          <div className="statement-summary">
            <div className="summary-card">
              <span className="label">Opening Balance</span>
              <span className="value">{formatCurrency(statement.openingBalance, statement.openingBalanceCurrency || cur)}</span>
            </div>
            <div className="summary-card highlight">
              <span className="label">Net Change</span>
              <span className={`value ${statement.netChange == null || isNaN(statement.netChange) ? '' : (statement.netChange >= 0 ? 'positive' : 'negative')}`}>
                {statement.netChange == null || isNaN(statement.netChange) ? 'N/A' : (statement.netChange >= 0 ? '+' : '-') + formatCurrency(Math.abs(statement.netChange), cur)}
              </span>
            </div>
            <div className="summary-card">
              <span className="label">Closing Balance</span>
              <span className="value">{fmt(statement.closingBalance)}</span>
            </div>
            <div className="summary-card">
              <span className="label">Total Credits</span>
              <span className="value positive">{fmt(statement.totalCredits)}</span>
            </div>
            <div className="summary-card">
              <span className="label">Total Debits</span>
              <span className="value negative">{fmt(statement.totalDebits)}</span>
            </div>
            <div className="summary-card">
              <span className="label">Entries</span>
              <span className="value">{statement.entryCount}</span>
            </div>
          </div>

          <div className="statement-actions">
            <div className="export-dropdown" ref={menuRef}>
              <button className="btn btn-ghost export-toggle" onClick={() => setShowExportMenu(v => !v)}>
                <Download size={14} /> Export <ChevronDown size={12} />
              </button>
              {showExportMenu && (
                <div className="export-menu">
                  <button onClick={handleExportCSV}>Export CSV</button>
                  <button onClick={handleExportPDF}>Export PDF</button>
                  <button onClick={handleExportExcel}>Export Excel (XLSX)</button>
                </div>
              )}
            </div>
            <span className="period-info">
              {statement.period.start} → {statement.period.end} &nbsp;|&nbsp; {cur}
            </span>
          </div>

          <table className="statement-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Description</th>
                <th className="text-right">Credit</th>
                <th className="text-right">Debit</th>
                <th className="text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {statement.entries.length === 0 ? (
                <tr><td colSpan="6" className="text-center">No transactions in this period</td></tr>
              ) : (
                statement.entries.map((entry, i) => (
                  <tr key={entry.transactionId || i}>
                    <td>{formatDate(entry.date)}</td>
                    <td><span className="type-badge">{entry.type}</span></td>
                    <td className="desc-cell">{entry.description || ''}</td>
                    <td className="text-right">{entry.credit > 0 ? formatCurrency(entry.credit, entry.creditCurrency || cur) : '-'}</td>
                    <td className="text-right">{entry.debit > 0 ? formatCurrency(entry.debit, entry.debitCurrency || cur) : '-'}</td>
                    <td className="text-right font-medium">{formatCurrency(entry.balance, entry.balanceCurrency || cur)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {statement.positionsInPeriod && statement.positionsInPeriod.length > 0 && (
            <div className="statement-section">
              <h4>Positions Opened ({statement.positionsInPeriod.length})</h4>
              <table className="statement-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Side</th>
                    <th>Qty</th>
                    <th className="text-right">Entry</th>
                    <th className="text-right">Leverage</th>
                    <th>Opened</th>
                  </tr>
                </thead>
                <tbody>
                  {statement.positionsInPeriod.map((p, i) => (
                    <tr key={p.tradeId || i}>
                      <td><strong>{stripSymbol(p.symbol)}</strong></td>
                      <td><span className={`type-badge side-${p.side}`}>{p.side.toUpperCase()}</span></td>
                      <td>{p.quantity}</td>
                      <td className="text-right">{formatPrice(p.entryPrice)}</td>
                      <td className="text-right">{p.leverage}x</td>
                      <td>{formatDate(p.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {statement.ordersInPeriod && statement.ordersInPeriod.length > 0 && (
            <div className="statement-section">
              <h4>Orders Placed ({statement.ordersInPeriod.length})</h4>
              <table className="statement-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Side</th>
                    <th>Type</th>
                    <th>Qty</th>
                    <th className="text-right">Price</th>
                    <th className="text-right">Lev</th>
                    <th>Status</th>
                    <th>Placed</th>
                  </tr>
                </thead>
                <tbody>
                  {statement.ordersInPeriod.map((o, i) => (
                    <tr key={o.orderId || i}>
                      <td><strong>{stripSymbol(o.symbol)}</strong></td>
                      <td><span className={`type-badge side-${o.side}`}>{o.side.toUpperCase()}</span></td>
                      <td>{o.orderType}</td>
                      <td>{o.quantity}</td>
                      <td className="text-right">{formatPrice(o.price)}</td>
                      <td className="text-right">{o.leverage}x</td>
                      <td><span className="type-badge">{o.status}</span></td>
                      <td>{formatDate(o.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {statement.tradeHistory && statement.tradeHistory.length > 0 && (
            <div className="statement-section">
              <h4>Trade History — Closed ({statement.tradeHistory.length})</h4>
              <table className="statement-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Side</th>
                    <th>Qty</th>
                    <th className="text-right">Entry</th>
                    <th className="text-right">Exit</th>
                    <th className="text-right">Gross P&L</th>
                    <th className="text-right">Net P&L</th>
                    <th className="text-right">Commission</th>
                    <th className="text-right">Swap</th>
                    <th className="text-right">Fees Deducted</th>
                    <th>Lev</th>
                    <th>Closed</th>
                  </tr>
                </thead>
                <tbody>
                  {statement.tradeHistory.map((t, i) => (
                    <tr key={t.tradeId || i}>
                      <td><strong>{stripSymbol(t.symbol)}</strong></td>
                      <td><span className={`type-badge side-${t.side}`}>{t.side.toUpperCase()}</span></td>
                      <td>{t.quantity}</td>
                      <td className="text-right">{formatPrice(t.entryPrice)}</td>
                      <td className="text-right">{formatPrice(t.exitPrice)}</td>
                      <td className={`text-right font-medium ${(t.grossPnl || 0) >= 0 ? 'text-positive' : 'text-danger'}`}>
                        {formatCurrency(t.grossPnl || 0, t.currency || cur)}
                      </td>
                      <td className={`text-right font-medium ${(t.pnl || 0) >= 0 ? 'text-positive' : 'text-danger'}`}>
                        {formatCurrency(t.pnl || 0, t.currency || cur)}
                      </td>
                      <td className="text-right">{formatCurrency(t.commission || 0, t.currency || cur)}</td>
                      <td className="text-right">{formatCurrency(t.swap || 0, t.currency || cur)}</td>
                      <td className="text-right">{formatCurrency(t.feesDeducted || 0, t.currency || cur)}</td>
                      <td>{t.leverage}x</td>
                      <td>{formatDate(t.closedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {statement.pagination && statement.pagination.pages > 1 && (
            <div className="pagination-info">
              Page {statement.pagination.page} of {statement.pagination.pages} ({statement.pagination.total} entries)
            </div>
          )}
        </div>
        );
      })()}
    </div>
  );
};

export default StatementPanel;
